import { useEffect, useState } from "react";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";

import C from "@constants/constants";

import useCampaignEmailStore from "@store/campaignStore";
import { useUserStore } from "@store/userStore";

const scopeDifference = (userScopeNylas: string[], scopePermissions: string[]): string[] =>
  scopePermissions.filter((x) => !userScopeNylas.includes(x));

const SendingOptions = () => {
  const { scheduledDateTime, sendOptions, setScheduledDateTime, setSendOptions } = useCampaignEmailStore();
  const { nylasPermissions } = useUserStore();

  const userScopeNylas: string[] = nylasPermissions ? nylasPermissions.split(" ") : [];
  const [allRequiredNylasPermissionsSet, setAllRequiredNylasPermissionsSet] = useState<boolean>(false);
  const [scopeDiff, setScopeDiff] = useState<string[]>([]);

  useEffect(() => {
    if (userScopeNylas.some((scp) => scp.includes("google"))) {
      const googleScopePermissions = [...C.NYLAS_PERMISSIONS_GOOGLE];
      setScopeDiff(scopeDifference(userScopeNylas, googleScopePermissions));
    } else {
      const microsoftScopePermissions = [...C.NYLAS_PERMISSIONS_MICROSOFT];
      setScopeDiff(scopeDifference(userScopeNylas, microsoftScopePermissions));
    }

    // If no differences, it means all required permissions are set
    setAllRequiredNylasPermissionsSet(scopeDiff.length === 0);
  }, [nylasPermissions, userScopeNylas]); // Re-run effect when nylasPermissions or userScopeNylas change

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Typography variant="h5">Sending Options</Typography>
      {allRequiredNylasPermissionsSet && (
        <Box>
          <Box
            sx={{
              alignItems: "flex-end",
              display: "flex",
              flexDirection: "row",
              gap: 1,
              justifyContent: "space-between",
            }}
          ></Box>
          <FormControl>
            <RadioGroup
              onChange={(event) => setSendOptions(event.target.value)}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
              value={sendOptions}
            >
              <FormControlLabel control={<Radio />} label="Send Immediately" value="send" />
              <FormControlLabel control={<Radio />} label="Schedule Send" value="schedule" />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      {!allRequiredNylasPermissionsSet && (
        <Box
          sx={{
            alignItems: "flex-start",
            background: "#EF6C00",
            borderRadius: 1,
            color: "white",
            display: "flex",
            flexDirection: "row",
            flexGrow: 0,
            fontFamily: "Open Sans",
            fontStyle: "normal",
            fontWeight: 600,
            gap: 2,
            padding: 2,
            width: "480px",
          }}
        >
          <Box sx={{ alignItems: "center", display: "flex", height: "100%" }}>
            <WarningAmberIcon />
          </Box>
          <Box sx={{ maxHeight: "100%" }}>
            <Typography sx={{ fontSize: 16, letterSpacing: "0.15px", pb: 1 }} variant="body1">
              Email Permissions Needed
            </Typography>
            <Typography sx={{ fontSize: 14, mb: 2 }} variant="body2">
              Your organization may not have enabled email permissions for Campaigns. Try reconnecting your email in
              your Account settings. Message us in the Chat if your issue continues.
            </Typography>
            <Typography sx={{ fontSize: 16 }} variant="body1">
              Missing Permissions:
            </Typography>
            <Box sx={{ pl: 2, width: "300px" }}>
              <List
                sx={{
                  display: "inline-block",
                  listStyleType: "disc",
                  marginBottom: 2,
                  maxWidth: "100%",
                  paddingLeft: 2,
                }}
              >
                {scopeDiff.map((item: string, index) => (
                  <ListItem key={index} sx={{ display: "list-item", maxWidth: "100%", p: 0 }}>
                    <ListItemText primary={item} sx={{ wordBreak: "break-word" }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          borderRadius: 1,
          flexGrow: 1,
          maxHeight: "calc(100vh - 407px)",
          maxWidth: "100%",
          overflowY: "auto",
          py: 2,
          width: "480px",
        }}
      >
        {sendOptions === "schedule" && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              closeOnSelect={false}
              disablePast
              label="Select send date & time..."
              onChange={setScheduledDateTime}
              sx={{
                width: "100%",
              }}
              value={scheduledDateTime}
            />
          </LocalizationProvider>
        )}
      </Box>
    </Box>
  );
};

export default SendingOptions;
